<template>
    <div
        class="transaction-row flex items-center gap-3 sm:grid sm:grid-cols-12 sm:gap-4 px-4 border-border-base hover:bg-bg-subtle cursor-pointer transition-colors"
        :class="[
            compact ? 'py-2' : 'py-3',
            // v2 mobile: no divider under the last row of a group; desktop keeps it
            isLastInGroup ? 'border-b-0 sm:border-b' : 'border-b',
        ]"
        @click="$emit('click', transaction)"
    >
        <!-- Merchant (Mobile: grows to fill, Desktop: 4-5 cols) -->
        <div
            class="flex items-center gap-3 flex-1 min-w-0"
            :class="showUser ? 'sm:col-span-4' : 'sm:col-span-5'"
        >
            <!-- Merchant chip: logo, else placeholder icon (v2 spec). A hidden
                 transaction gets an eye-off mini-badge overlaid on the chip. -->
            <div class="relative flex-shrink-0">
                <div
                    v-if="!transaction.merchant_logo_url"
                    class="w-10 h-10 rounded-full bg-bg-base flex items-center justify-center"
                >
                    <Icon
                        :icon="iconForCategory(transaction.category)"
                        class="w-4 h-4 text-fg-secondary"
                    />
                </div>
                <img
                    v-else
                    :src="transaction.merchant_logo_url"
                    :alt="transaction.merchant_name"
                    class="w-10 h-10 rounded-full object-cover"
                />
                <span
                    v-if="transaction.is_hidden"
                    v-tooltip="'Transaction hidden from list'"
                    class="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-bg-snow border border-border-base flex items-center justify-center"
                >
                    <Icon icon="eye-off" class="w-3 h-3 text-fg-secondary" />
                </span>
            </div>

            <!-- Merchant Name -->
            <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0">
                    <span
                        v-tooltip="
                            (transaction.merchant_name || '').length > 48
                                ? transaction.merchant_name
                                : null
                        "
                        class="body-lg-500 sm:body-md-500 text-fg-primary line-clamp-2 break-words"
                    >
                        {{ transaction.merchant_name }}
                    </span>
                    <span
                        v-if="transaction.linked_benefits?.length"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 flex-shrink-0"
                        :title="
                            transaction.linked_benefits
                                .map((b) => b.benefit_title)
                                .join(', ')
                        "
                    >
                        Benefit
                    </span>
                    <span
                        v-else-if="transaction.suggested_benefits?.length"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 flex-shrink-0"
                        :title="
                            transaction.suggested_benefits
                                .map((b) => b.benefit_title)
                                .join(', ')
                        "
                    >
                        Suggested
                    </span>
                </div>
                <!-- Category subtitle (desktop) -->
                <div class="hidden sm:block mt-0.5 min-w-0">
                    <span
                        v-tooltip="
                            (transaction.category || '').length > 28
                                ? transaction.category
                                : null
                        "
                        class="body-md text-fg-tertiary truncate block"
                    >
                        {{ transaction.category }}
                    </span>
                </div>
                <!-- Card subtitle (mobile: card replaces its own column) -->
                <div class="flex sm:hidden items-center gap-1.5 mt-0.5 min-w-0">
                    <span class="body-md text-fg-tertiary truncate">
                        {{ transaction.card_name }}
                        <span
                            v-if="showCardNumber && transaction.card_last_four"
                            class="text-fg-tertiary/70"
                        >
                            ({{ transaction.card_last_four }})
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <!-- User (Desktop: 2 cols, multi-user only) -->
        <div
            v-if="showUser"
            class="hidden sm:flex sm:col-span-2 items-center gap-2 min-w-0"
        >
            <Icon
                :icon="userIsBusiness ? 'business' : 'user'"
                class="w-4 h-4 text-fg-tertiary flex-shrink-0"
            />
            <span class="body-md text-fg-secondary truncate">
                {{ userFirstName }}
            </span>
        </div>

        <!-- Account/Card (Desktop: 2-3 cols; mobile shows card as merchant subtitle) -->
        <div
            class="hidden sm:flex items-center"
            :class="showUser ? 'sm:col-span-2' : 'sm:col-span-3'"
        >
            <div
                v-tooltip="
                    (transaction.card_name || '').length > 22
                        ? `${transaction.card_name}${transaction.card_last_four ? ` (${transaction.card_last_four})` : ''}`
                        : null
                "
                class="flex items-center gap-2 min-w-0"
            >
                <img
                    v-if="cardImageUrl"
                    :src="cardImageUrl"
                    :alt="transaction.card_name"
                    class="w-8 h-5 rounded-sm object-cover flex-shrink-0"
                />
                <div
                    v-else
                    class="w-8 h-5 rounded-sm bg-gradient-to-r from-gray-700 to-gray-500 flex-shrink-0"
                ></div>
                <span class="body-md text-fg-secondary truncate">
                    {{ transaction.card_name }}
                </span>
                <span
                    v-if="showCardNumber && transaction.card_last_four"
                    class="body-md text-fg-tertiary flex-shrink-0"
                >
                    ({{ transaction.card_last_four }})
                </span>
            </div>
        </div>

        <!-- Rewards (Desktop: 2 cols). Points right-aligned to a shared
             boundary; program logos left-aligned in a fixed slot (v2 spec). -->
        <div class="hidden sm:flex sm:col-span-2 items-center justify-end gap-2">
            <template v-if="estimatedRewards">
                <span
                    v-tooltip="transaction.program?.name || null"
                    class="inline-flex items-baseline gap-1"
                >
                    <span class="body-md-500 text-fg-primary">
                        {{ formatNumber(estimatedRewards.points) }}
                    </span>
                    <span class="body-md text-fg-tertiary">pts</span>
                </span>
                <span class="w-14 flex-shrink-0 flex justify-start">
                    <img
                        v-if="programImageUrl"
                        :src="programImageUrl"
                        :alt="transaction.program?.name"
                        class="h-4 w-auto max-w-full object-contain"
                    />
                </span>
            </template>
            <template v-else>
                <span class="body-md text-fg-tertiary">—</span>
                <span class="w-14 flex-shrink-0"></span>
            </template>
        </div>

        <!-- Amount (+ mobile pts) + row menu (Mobile: right aligned, Desktop: 2 cols) -->
        <div class="flex items-center justify-end gap-1 flex-shrink-0 sm:col-span-2">
            <div class="flex flex-col items-end">
                <span
                    class="body-lg-500 sm:body-md"
                    :class="
                        isCredit
                            ? 'text-success font-medium'
                            : 'text-fg-primary'
                    "
                >
                    {{ formatAmount(transaction.amount) }}
                </span>
                <!-- Points under amount on mobile (desktop uses the Rewards column) -->
                <span
                    v-if="estimatedRewards"
                    class="sm:hidden body-md text-fg-tertiary"
                >
                    {{ formatNumber(estimatedRewards.points) }} pts
                </span>
            </div>
            <!-- Row menu (desktop only; mobile opens details on tap, v2 spec) -->
            <div v-tooltip="'Actions'" class="hidden sm:block" @click.stop>
                <ButtonPopover
                    icon="dots-vertical"
                    iconClass="w-5 h-5 text-fg-tertiary"
                    buttonClass="!p-1.5 rounded-md hover:bg-bg-base"
                    placement="bottom-end"
                    popoverClass="bg-bg-snow rounded-lg shadow-lg min-w-44 z-30 outline outline-1 outline-offset-[-1px] outline-border-base p-1.5"
                >
                    <template #default="{ closePopover }">
                        <button
                            type="button"
                            class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle transition-colors text-left"
                            @click="
                                () => {
                                    $emit('view', transaction);
                                    closePopover();
                                }
                            "
                        >
                            <Icon
                                icon="document"
                                :width="18"
                                :height="18"
                                class="text-slate-500"
                            />
                            View details
                        </button>
                        <button
                            type="button"
                            class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle transition-colors text-left"
                            @click="
                                () => {
                                    canEdit
                                        ? $emit('hide', transaction)
                                        : $emit('upgrade');
                                    closePopover();
                                }
                            "
                        >
                            <Icon
                                :icon="transaction.is_hidden ? 'eye-on' : 'eye-off'"
                                :width="18"
                                :height="18"
                                class="text-slate-500"
                            />
                            {{
                                transaction.is_hidden
                                    ? 'Show transaction'
                                    : 'Hide transaction'
                            }}
                        </button>
                        <button
                            type="button"
                            class="w-full flex items-center gap-2 p-2 body-md text-danger rounded-md hover:bg-bg-subtle transition-colors text-left"
                            @click="
                                () => {
                                    canEdit
                                        ? $emit('delete', transaction)
                                        : $emit('upgrade');
                                    closePopover();
                                }
                            "
                        >
                            <Icon icon="trash" :width="18" :height="18" />
                            Delete
                        </button>
                    </template>
                </ButtonPopover>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import { iconForCategory } from '@/utils/category-icons';

const props = defineProps({
    transaction: {
        type: Object,
        required: true,
    },
    compact: {
        type: Boolean,
        default: false,
    },
    showCardNumber: {
        type: Boolean,
        default: true,
    },
    // Multi-user: show the User column (avatar + first name) when true.
    showUser: {
        type: Boolean,
        default: false,
    },
    userName: {
        type: String,
        default: '',
    },
    userIsBusiness: {
        type: Boolean,
        default: false,
    },
    // When false (free users), mutating row actions emit 'upgrade' instead.
    canEdit: {
        type: Boolean,
        default: true,
    },
    // Last row of its date group: mobile drops the divider (v2 spec).
    isLastInGroup: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['click', 'view', 'hide', 'delete', 'upgrade']);

// Show just the first name in the compact User column (e.g. "Nick Smith" → "Nick").
const userFirstName = computed(() => (props.userName || '').split(' ')[0] || '');


// Determine if this is a credit (refund/return) vs debit (expense)
// Warehouse returns is_expense flag and type field
const isCredit = computed(() => {
    // If is_income is set, it's a credit
    if (props.transaction.is_income) {
        return true;
    }
    // If type is CREDIT, it's a credit
    if (props.transaction.type === 'CREDIT') {
        return true;
    }
    // If is_expense is set, it's NOT a credit
    if (props.transaction.is_expense) {
        return false;
    }
    // Fallback: negative amounts are debits, positive are credits
    return props.transaction.amount < 0;
});

const formatAmount = (amount) => {
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Math.abs(amount));

    // Only show + prefix for true credits (refunds/returns)
    return isCredit.value ? `+${formatted}` : formatted;
};

const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
};

// Construct card image URL from card_image data
const cardImageUrl = computed(() => {
    const cardImage = props.transaction.card_image;
    if (!cardImage?.path || !cardImage?.file_name) {
        return null;
    }
    const cdnUrl = window.cdnUrl || '';
    return `${cdnUrl}/${cardImage.path}/_small/${cardImage.file_name}`;
});

// Construct program image URL from program data
const programImageUrl = computed(() => {
    const programImage = props.transaction.program?.image;
    if (!programImage?.path || !programImage?.file_name) {
        return null;
    }
    const cdnUrl = window.cdnUrl || '';
    return `${cdnUrl}/${programImage.path}/_small/${programImage.file_name}`;
});

// Estimate rewards earned based on transaction amount and earn rate
// Uses calculated_earn_rate from backend (based on card bonus categories)
// Falls back to earn_rate (user override) or 1x if neither available
const estimatedRewards = computed(() => {
    const amount = Math.abs(props.transaction.amount || 0);

    // Skip if amount is zero
    if (amount === 0) {
        return null;
    }

    // Skip if it's income (not an expense) - warehouse returns is_expense flag
    // Also check type === 'CREDIT' as fallback
    const isExpense =
        props.transaction.is_expense || props.transaction.type === 'DEBIT';
    if (!isExpense) {
        return null;
    }

    // Skip if no card is associated (Unknown Card)
    if (
        !props.transaction.card_name ||
        props.transaction.card_name === 'Unknown Card'
    ) {
        return null;
    }

    // Use user-set earn_rate first, then calculated_earn_rate from backend, then default to 1x
    const earnRate =
        props.transaction.earn_rate ||
        props.transaction.calculated_earn_rate ||
        1;
    const points = Math.floor(amount * earnRate);

    return {
        points,
        rate: earnRate,
        isBonus: props.transaction.is_bonus_category || false,
        categoryName: props.transaction.earn_rate_category || null,
    };
});
</script>

<style scoped>
.transaction-row:last-child {
    border-bottom: none;
}
</style>
